import React, { useMemo, useRef, useState } from 'react';
import { FiActivity, FiLock } from 'react-icons/fi';
import { motion } from 'framer-motion';
import SafeIcon from './common/SafeIcon';
import AdminModal from './components/AdminModal';
import BatchControls from './components/BatchControls';
import ConfidenceMeter from './components/ConfidenceMeter';
import HistoryPanel from './components/HistoryPanel';
import NumberGrid from './components/NumberGrid';
import SelectionSummary from './components/SelectionSummary';
import SeoContentSection from './components/SeoContentSection';
import {
  createVariant,
  generateVariantBatch,
  rankVariants
} from './utils/lotoAlgorithm';
import { logGeneration } from './utils/generationTracker';
import './App.css';

const initialVariant = createVariant(1);

function App() {
  const nextId = useRef(2);
  const [current, setCurrent] = useState(initialVariant);
  const [history, setHistory] = useState([]);
  const [batchSize, setBatchSize] = useState(15);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isAdminOpen, setIsAdminOpen] = useState(false);

  const selected = useMemo(
    () => history.filter((variant) => selectedIds.includes(variant.id)),
    [history, selectedIds]
  );

  const generate = () => {
    const batch = rankVariants(generateVariantBatch(batchSize, nextId.current));
    nextId.current += batchSize;
    setCurrent(batch[0]);
    setHistory((existing) => [...batch, ...existing]);
    setSelectedIds(batch.slice(0, Math.min(3, batch.length)).map(({ id }) => id));
    
    // Loghează activitatea de generare pentru panoul de administrare
    logGeneration(batchSize);
  };

  const toggleSelection = (id) => {
    setSelectedIds((existing) => {
      if (existing.includes(id)) {
        return existing.filter((selectedId) => selectedId !== id);
      }
      return existing.length < 3 ? [...existing, id] : existing;
    });
  };

  const clearHistory = () => {
    setHistory([]);
    setSelectedIds([]);
  };

  return (
    <main className="app-shell">
      <header className="topbar">
        <a className="brand" href="#simulator">L6/49—SIM</a>
        <div className="topbar-actions">
          <span><i /> SISTEM ONLINE • ROMÂNIA</span>
          <button
            type="button"
            className="admin-access-btn"
            onClick={() => setIsAdminOpen(true)}
            title="Panou Administrare"
          >
            <SafeIcon icon={FiLock} />
            ADMIN
          </button>
        </div>
      </header>

      <section className="hero" id="simulator">
        <div className="eyebrow">
          <SafeIcon icon={FiActivity} />
          GENERATOR NUMERE LOTO 6/49 & SIMULATOR STATISTIC ROMÂNIA
        </div>
        <h1>
          SIMULATOR
          <br />
          <em>LOTO 6/49</em>
        </h1>
        <p className="intro">
          Folosește acest <strong>generator numere loto 6/49</strong> pentru a genera și evalua rapid 
          <strong> variante loto 6 din 49</strong>. Testează loturi de 15 numere, compară 
          scorul de distribuție statistică și consultă ghidul de <strong>scheme reduse loto</strong> (cod 57 &amp; cod 58).
        </p>
      </section>

      <motion.section
        className="generator-panel"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="panel-head">
          <span>VARIANTA RECOMANDATĂ (SCOR DISTRIBUȚIE MAXIM)</span>
          <span>INTERVAL [01—49]</span>
        </div>
        <NumberGrid numbers={current.numbers} drawId={current.id} />
        <ConfidenceMeter
          distributionScore={current.distributionScore}
          confidenceScore={current.confidenceScore}
        />
        <BatchControls
          batchSize={batchSize}
          onBatchSizeChange={setBatchSize}
          onGenerate={generate}
        />
      </motion.section>

      <SelectionSummary selected={selected} />

      <HistoryPanel
        variants={history}
        selectedIds={selectedIds}
        onSelect={toggleSelection}
        onClear={clearHistory}
      />

      <SeoContentSection />

      <footer>
        <div>
          <span>ALGORITM: SECURE_UNIFORM_V3</span>
          <p className="footer-keywords">
            Generator numere 6/49 • Simulare Loto • Scheme reduse loto 6 49 cod 57 / 58 • Variante loto 6 din 49
          </p>
        </div>
        <p>
          Simulator statistic independent. Nu garantăm câștiguri financiare. Toate combinațiile
          posibile au șanse matematice identice conform teoriei probabilităților.
        </p>
      </footer>

      <AdminModal
        isOpen={isAdminOpen}
        onClose={() => setIsAdminOpen(false)}
      />
    </main>
  );
}

export default App;